import { SQDCActionRevisionFieldType, SQDCEventRevisionFieldType } from "@/graphql";

type RevisionFieldType = SQDCActionRevisionFieldType | SQDCEventRevisionFieldType;
type Translate = (key: string) => string;

export type RevisionDisplayFormatters = {
  formatCustomFieldLabel: (fieldId?: number) => string;
  formatCustomFieldValue: (fieldId: number | undefined, value: unknown) => string;
  formatDate: (value?: string) => string;
  formatDatapoints: (value: unknown) => string;
  formatEventTypeConversion?: (value: unknown) => string;
  formatFlags: (value: unknown) => string;
  formatMove: (value: unknown) => string;
  formatPlain: (value: unknown) => string;
  formatStatus: (value: unknown) => string;
  formatUser: (value: unknown) => string;
  formatValueIds: (value: unknown) => string;
  hasCustomFieldAccess?: (fieldId?: number) => boolean;
  t: Translate;
};

export function getRevisionFieldLabel(
  fieldType: RevisionFieldType,
  fieldId: number | undefined,
  formatters: RevisionDisplayFormatters,
) {
  if (
    fieldType === SQDCActionRevisionFieldType.CUSTOM_FIELD ||
    fieldType === SQDCEventRevisionFieldType.CUSTOM_FIELD
  ) {
    return hasCustomFieldAccess(fieldId, formatters)
      ? formatters.formatCustomFieldLabel(fieldId)
      : formatters.t("global.na");
  }

  switch (fieldType) {
    case SQDCEventRevisionFieldType.DATE:
      return formatters.t("components.issueModal.date");
    case SQDCEventRevisionFieldType.CREATOR:
      return formatters.t("components.issueModal.user");
    case SQDCEventRevisionFieldType.EISENHOWER:
      return formatters.t("components.issueModal.eisenhowerPriority");
    case SQDCEventRevisionFieldType.FLAGS:
      return formatters.t("components.issueModal.area");
    case SQDCEventRevisionFieldType.ARCHIVE:
      return formatters.t(
        "modules.sqdc.components.eventActionBoard.EventActionRevisionList.archive",
      );
    case SQDCEventRevisionFieldType.LINKAGE:
      return formatters.t("components.event-action.issue.AbstractIssue.linkedEvents");
    case SQDCEventRevisionFieldType.DATAPOINTS:
      return formatters.t(
        "modules.sqdc.components.eventActionBoard.modals.SQDCRevisionModal.datapoints",
      );
    case SQDCEventRevisionFieldType.MOVE:
      return formatters.t(
        "modules.sqdc.components.eventActionBoard.modals.SQDCModalEventActionSettings.moveEvent",
      );
    case SQDCEventRevisionFieldType.TYPE_CONVERSION:
      return formatters.t(
        "modules.sqdc.components.eventActionBoard.modals.SQDCEventTypeConversionModal.historyLabel",
      );
    case SQDCActionRevisionFieldType.DEADLINE:
      return formatters.t(
        "modules.sqdc.components.eventActionBoard.EventActionRevisionList.deadline",
      );
    case SQDCActionRevisionFieldType.STATUS:
      return "PDCA";
    case SQDCActionRevisionFieldType.ASSIGNEE:
      return formatters.t(
        "modules.sqdc.components.eventActionBoard.EventActionRevisionList.assignee",
      );
    default:
      return formatters.t("global.na");
  }
}

export function formatRevisionFieldValue(
  fieldType: RevisionFieldType,
  value: unknown,
  fieldId: number | undefined,
  formatters: RevisionDisplayFormatters,
) {
  if (value == null || value === "") {
    return "";
  }

  switch (fieldType) {
    case SQDCEventRevisionFieldType.DATE:
    case SQDCActionRevisionFieldType.DEADLINE:
      return typeof value === "string" ? formatters.formatDate(value) : String(value);
    case SQDCEventRevisionFieldType.CREATOR:
    case SQDCActionRevisionFieldType.ASSIGNEE:
      return formatters.formatUser(value);
    case SQDCEventRevisionFieldType.EISENHOWER:
      return formatters.t(`components.eventAction.filter.badges.PriorityFilter.${value}`);
    case SQDCEventRevisionFieldType.ARCHIVE:
      return value ? formatters.t("global.yes") : formatters.t("global.no");
    case SQDCActionRevisionFieldType.STATUS:
      return formatters.formatStatus(value);
    case SQDCEventRevisionFieldType.FLAGS:
      return formatters.formatFlags(value);
    case SQDCEventRevisionFieldType.LINKAGE:
      return formatters.formatValueIds(value);
    case SQDCEventRevisionFieldType.DATAPOINTS:
      return formatters.formatDatapoints(value);
    case SQDCEventRevisionFieldType.MOVE:
      return formatters.formatMove(value);
    case SQDCEventRevisionFieldType.TYPE_CONVERSION:
      return formatters.formatEventTypeConversion?.(value) ?? formatters.formatPlain(value);
    case SQDCActionRevisionFieldType.CUSTOM_FIELD:
    case SQDCEventRevisionFieldType.CUSTOM_FIELD:
      return hasCustomFieldAccess(fieldId, formatters)
        ? formatters.formatCustomFieldValue(fieldId, value)
        : "";
    default:
      return formatters.formatPlain(value);
  }
}

export function formatDatapointRevisionValue(
  value: unknown,
  formatDate: (value?: string) => string,
  formatPlain: (value: unknown) => string,
) {
  if (!Array.isArray(value)) return formatPlain(value);
  return value
    .map((entry) => {
      const datapoint = parseRecordValue(entry);
      if (datapoint == null) return formatPlain(entry);
      const type = datapoint.datapointTypeName || `#${datapoint.typeId ?? ""}`;
      const costCenter = datapoint.costCenterName || `#${datapoint.costCenterId ?? ""}`;
      const timestamp =
        typeof datapoint.timestamp === "string" ? formatDate(datapoint.timestamp) : "";
      return [type, datapoint.quantity, costCenter, timestamp]
        .filter((part) => part != null && part !== "")
        .join(" · ");
    })
    .join("; ");
}

function hasCustomFieldAccess(fieldId: number | undefined, formatters: RevisionDisplayFormatters) {
  return formatters.hasCustomFieldAccess?.(fieldId) ?? true;
}

function parseRecordValue(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string" || value.trim() === "") {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}
